'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useParams } from 'next/navigation';

import Container from '@/components/ui/Container';
import Text from '@/components/ui/Text';
import Copyright from '@/features/footer/Copyright';
import { MenuItem } from '@/features/header/NavList';
import { ContactInfo, SocialMediaItem } from '@/lib/contentful/api/getFooter';
import { normalizeLocale } from '@/lib/i18n/contactFormI18n';
import { COPYRIGHT_I18N } from '@/lib/i18n/copyright';
import { COMMON_I18N } from '@/lib/i18n/commonI18n';
import { useNavBehavior } from '@/hooks/useNavBehaviour';
import ContactMap from './ContactMap';

const underline =
  'pointer-events-none absolute left-0 -bottom-[2px] h-[2px] w-full origin-left scale-x-0 bg-secondary transition-transform duration-300 ease-out group-hover:scale-x-100';

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Text as="p" variant="sectionEyebrow" className="text-white/60 mb-6 text-xs">
    {children}
  </Text>
);

const Footer = ({
  socialMediaCollection,
  navigationItems,
  contactInfo,
  googleMapsIframe,
  googleMapsUrl,
  firmName,
  slogan,
}: FooterProps) => {
  const { locale: rawLocale } = useParams<{ locale: string }>();
  const locale = normalizeLocale(rawLocale);
  const { contact, links } = COPYRIGHT_I18N[locale];
  const common = COMMON_I18N[locale];
  const { handleClick } = useNavBehavior(navigationItems);

  return (
    <footer className="bg-primary relative z-40">
      <Container className="py-section-y-sm tablet:py-section-y">
        {/* Main grid */}
        <div className="grid grid-cols-1 desktop:grid-cols-3 gap-12 desktop:gap-8">
          {/* Brand */}
          <div className="desktop:col-span-1">
            <Text as="h2" variant="footerTitle">
              {firmName}
            </Text>
            {slogan && (
              <Text
                as="p"
                variant="paragraph"
                className="text-white/60 mt-2 mb-0!"
              >
                {slogan}
              </Text>
            )}
          </div>

          {/* Contact */}
          <div>
            <SectionLabel>{contact}</SectionLabel>
            <div className="flex flex-col gap-4">
              <Link
                href={`mailto:${contactInfo.email}`}
                className="group relative w-fit"
              >
                <Text as="span" variant="footerLink">
                  {contactInfo.email}
                </Text>
                <span aria-hidden className={underline} />
              </Link>
              <Link
                href={`tel:${contactInfo.phone}`}
                className="group relative w-fit"
              >
                <Text as="span" variant="footerLink">
                  {contactInfo.phone}
                </Text>
                <span aria-hidden className={underline} />
              </Link>
              <Link
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-block"
              >
                <Text as="span" variant="footerLink">
                  {contactInfo.address}
                </Text>
                <span aria-hidden className={underline} />
              </Link>
              {socialMediaCollection?.length > 0 && (
                <div className="mt-2 flex gap-5">
                  {socialMediaCollection.map((item) => (
                    <Link
                      key={item.url}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={item.name}
                      className="opacity-70 hover:opacity-100 transition-opacity duration-200"
                    >
                      <Image
                        src={item.iconUrl}
                        alt=""
                        width={24}
                        height={24}
                        className="h-5 w-auto"
                      />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav aria-label={common.footerNavigation}>
            <SectionLabel>{links}</SectionLabel>
            <div className="flex flex-col gap-4">
              {navigationItems.map((item) => (
                <Link
                  key={item.id}
                  href={item.href}
                  scroll={!item.href.includes('#')}
                  onClick={(e) => handleClick(e, item)}
                  className="group relative w-fit"
                >
                  <Text as="span" variant="footerLink">
                    {item.name}
                  </Text>
                  <span aria-hidden className={underline} />
                </Link>
              ))}
            </div>
          </nav>
        </div>

        {/* Contact Map */}
        <ContactMap
          googleMapsIframe={googleMapsIframe}
          title={locale === 'pt' ? 'Localização no mapa' : 'Location map'}
          className="mt-12"
        />
      </Container>

      <div className="border-t border-white/10 py-5">
        <Copyright firmName={firmName} />
      </div>
    </footer>
  );
};

export default Footer;

type FooterProps = {
  socialMediaCollection: SocialMediaItem[];
  navigationItems: MenuItem[];
  contactInfo: ContactInfo;
  googleMapsUrl: string;
  googleMapsIframe: string;
  firmName: string;
  slogan?: string;
};
